import React, { useEffect, useState } from 'react'
import { useReminderSettings } from '@/hooks/use-reminder-settings'
import { reminderService } from '@/services/reminderService'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Bell,
  BellOff,
  Calendar,
  Clock,
  Globe,
  Loader2,
  Mail,
  MessageSquare,
  Send,
  AlertTriangle,
  CheckCircle2,
  Info,
  Save
} from 'lucide-react'
import { toast } from 'sonner'
import type { DayOfWeek, ReminderChannels } from '@weekly-report/shared-types'

interface ReminderPreferencesPanelProps {
  className?: string
}

/**
 * ReminderPreferencesPanel
 * 
 * User interface for managing weekly reminder preferences including:
 * - Enable/disable reminders
 * - Timezone selection
 * - Day of week and time selection
 * - Channel preferences (email, Slack, Teams, in-app)
 * - Send now button for immediate reminders
 */
const ReminderPreferencesPanel: React.FC<ReminderPreferencesPanelProps> = ({ className }) => {
  const {
    settings,
    nextSendAt,
    remindersEnabled: globalRemindersEnabled,
    loading,
    error,
    saving,
    updateSettings,
    remindNow,
    refetch
  } = useReminderSettings()

  const [sendingNow, setSendingNow] = useState(false)
  const [localEnabled, setLocalEnabled] = useState(false)
  const [localTimezone, setLocalTimezone] = useState('')
  const [localDayOfWeek, setLocalDayOfWeek] = useState<DayOfWeek>(4)
  const [localTime, setLocalTime] = useState('09:00')
  const [localChannels, setLocalChannels] = useState<ReminderChannels>({
    email: false,
    slack: true,
    teams: false,
    inApp: false
  })
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  // Sync local state with fetched settings
  useEffect(() => {
    if (settings) {
      setLocalEnabled(settings.enabled)
      setLocalTimezone(settings.timezone || reminderService.getBrowserTimezone())
      setLocalDayOfWeek(settings.dayOfWeek)
      setLocalTime(settings.time)
      setLocalChannels(settings.channels)
      setHasUnsavedChanges(false)
    }
  }, [settings])

  // Check for unsaved changes
  useEffect(() => {
    if (settings) {
      const hasChanges = 
        localEnabled !== settings.enabled ||
        localTimezone !== (settings.timezone || reminderService.getBrowserTimezone()) ||
        localDayOfWeek !== settings.dayOfWeek ||
        localTime !== settings.time ||
        JSON.stringify(localChannels) !== JSON.stringify(settings.channels)
      setHasUnsavedChanges(hasChanges)
    }
  }, [localEnabled, localTimezone, localDayOfWeek, localTime, localChannels, settings])

  const timezones = reminderService.getCommonTimezones()
  const daysOfWeek = reminderService.getDayOfWeekOptions()

  const handleEnabledChange = (enabled: boolean) => {
    setLocalEnabled(enabled)
  }

  const handleTimezoneChange = (timezone: string) => {
    setLocalTimezone(timezone)
  }

  const handleDayOfWeekChange = (day: string) => {
    const dayNum = parseInt(day, 10) as DayOfWeek
    setLocalDayOfWeek(dayNum)
  }

  const handleTimeChange = (time: string) => {
    setLocalTime(time)
  }

  const handleChannelChange = (channel: keyof ReminderChannels, enabled: boolean) => {
    const newChannels = { ...localChannels, [channel]: enabled }
    setLocalChannels(newChannels)
  }

  const handleSaveSettings = async () => {
    if (!hasUnsavedChanges) return

    try {
      await updateSettings({
        enabled: localEnabled,
        timezone: localTimezone,
        dayOfWeek: localDayOfWeek,
        time: localTime,
        channels: localChannels
      })
      toast.success('Reminder settings saved successfully!')
      setHasUnsavedChanges(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save settings')
    }
  }

  const handleSendNow = async () => {
    setSendingNow(true)
    try {
      await remindNow()
      toast.success('Reminder sent successfully!')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send reminder')
    } finally {
      setSendingNow(false)
    }
  }

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64 mt-2" />
        </CardHeader>
        <CardContent className="space-y-6">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Error Loading Preferences
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">{error.message}</p>
          <Button variant="outline" className="mt-4" onClick={refetch}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-primary" />
          Reminder Preferences
        </CardTitle>
        <CardDescription>
          Configure when and how you want to receive weekly report reminders
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Global reminders disabled banner */}
        {!globalRemindersEnabled && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Automatic reminders are currently disabled by the administrator.
              You can still configure your preferences for when they&apos;re enabled.
            </AlertDescription>
          </Alert>
        )}

        {/* Enable/Disable Toggle */}
        <div className="flex items-center justify-between rounded-lg border border-border p-4">
          <div className="flex items-center gap-3">
            {localEnabled ? (
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Bell className="h-5 w-5 text-primary" />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                <BellOff className="h-5 w-5 text-muted-foreground" />
              </div>
            )}
            <div>
              <Label htmlFor="reminders-enabled" className="text-base font-medium">
                Weekly Reminders
              </Label>
              <p className="text-sm text-muted-foreground">
                {localEnabled ? 'You will receive weekly report reminders' : 'Reminders are disabled'}
              </p>
            </div>
          </div>
          <Switch
            id="reminders-enabled"
            checked={localEnabled}
            onCheckedChange={handleEnabledChange}
            disabled={saving}
          />
        </div>

        {/* Schedule Settings */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Schedule
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Timezone */}
            <div className="space-y-2">
              <Label htmlFor="timezone" className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-muted-foreground" />
                Timezone
              </Label>
              <Select
                value={localTimezone}
                onValueChange={handleTimezoneChange}
                disabled={saving}
              >
                <SelectTrigger id="timezone">
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent>
                  {timezones.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Day of Week */}
            <div className="space-y-2">
              <Label htmlFor="day-of-week" className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                Day of Week
              </Label>
              <Select
                value={String(localDayOfWeek)}
                onValueChange={handleDayOfWeekChange}
                disabled={saving}
              >
                <SelectTrigger id="day-of-week">
                  <SelectValue placeholder="Select day" />
                </SelectTrigger>
                <SelectContent>
                  {daysOfWeek.map((day) => (
                    <SelectItem key={day.value} value={String(day.value)}>
                      {day.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Time */}
            <div className="space-y-2">
              <Label htmlFor="reminder-time" className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                Time
              </Label>
              <Select
                value={localTime}
                onValueChange={handleTimeChange}
                disabled={saving}
              >
                <SelectTrigger id="reminder-time">
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, i) => {
                    const hour = i.toString().padStart(2, '0')
                    const value = `${hour}:00`
                    const date = new Date(2024, 0, 1, i, 0)
                    const label = date.toLocaleTimeString(undefined, {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    })
                    return (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    )
                  })}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Reminders are sent at the top of each hour. Select the hour you want to receive your reminder.
              </p>
            </div>
          </div>
        </div>

        {/* Channel Preferences */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Notification Channels
          </h4>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Slack */}
            <div className="flex items-center space-x-3 rounded-lg border border-border p-3">
              <Checkbox
                id="channel-slack"
                checked={localChannels.slack}
                onCheckedChange={(checked) => handleChannelChange('slack', !!checked)}
                disabled={saving}
              />
              <Label
                htmlFor="channel-slack"
                className="flex items-center gap-2 cursor-pointer flex-1"
              >
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
                Slack
              </Label>
            </div>

            {/* Email (disabled for now) */}
            <div className="flex items-center space-x-3 rounded-lg border border-border p-3 opacity-50">
              <Checkbox
                id="channel-email"
                checked={localChannels.email}
                disabled={true}
              />
              <Label
                htmlFor="channel-email"
                className="flex items-center gap-2 cursor-not-allowed flex-1"
              >
                <Mail className="h-4 w-4 text-muted-foreground" />
                Email
                <span className="text-xs text-muted-foreground">(Coming soon)</span>
              </Label>
            </div>
          </div>
        </div>

        {/* Save Settings Button */}
        {hasUnsavedChanges && (
          <div className="rounded-lg border border-warning/50 bg-warning/10 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <AlertTriangle className="h-4 w-4 text-warning" />
                <span className="font-medium text-warning">You have unsaved changes</span>
              </div>
              <Button
                onClick={handleSaveSettings}
                disabled={saving || !hasUnsavedChanges}
                className="gap-2"
                variant="default"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Save Settings
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Next Reminder Info */}
        {localEnabled && nextSendAt && settings && !hasUnsavedChanges && (
          <div className="rounded-lg bg-secondary/30 p-4">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="h-4 w-4 text-success" />
              <span className="font-medium">Next Reminder:</span>
              <span className="text-muted-foreground">
                {reminderService.formatNextSendTime(nextSendAt, settings.timezone || localTimezone)}
              </span>
            </div>
          </div>
        )}

        {/* Send Now Button */}
        <div className="pt-4 border-t border-border">
          <Button
            onClick={handleSendNow}
            disabled={!localEnabled || sendingNow}
            className="gap-2"
          >
            {sendingNow ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Send Reminder Now
              </>
            )}
          </Button>
          <p className="text-xs text-muted-foreground mt-2">
            Send yourself a reminder immediately using your configured channels
          </p>
        </div>
      </CardContent>
    </Card>
  )
}

export default ReminderPreferencesPanel

